import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty,
  CommandGroup, CommandItem, CommandSeparator,
} from "../ui/command";
import { Button } from "../ui/button";
import {
  Search, Users, Shield, Building2, LayoutDashboard, Package,
  Factory, Warehouse, ClipboardCheck, ShoppingCart, CalendarClock,
  Landmark, Wrench, UserCog, Lock, FileText, BarChart3, ArrowRight,
  Star, Clock, Bookmark, X, Truck, Box, Receipt, UserCheck, Layers,
  Sparkles, CheckSquare, StickyNote, TrendingUp, Zap,
} from "lucide-react";

const TYPE_CONFIG = {
  sales_order: { icon: ShoppingCart, label: "Sales Orders", color: "text-blue-500" },
  purchase_order: { icon: Package, label: "Purchase Orders", color: "text-orange-500" },
  invoice: { icon: Receipt, label: "Invoices", color: "text-emerald-500" },
  job_order: { icon: Factory, label: "Job Orders", color: "text-purple-500" },
  item: { icon: Box, label: "Items", color: "text-cyan-500" },
  customer: { icon: Building2, label: "Customers", color: "text-teal-500" },
  supplier: { icon: Truck, label: "Suppliers", color: "text-amber-500" },
  lead: { icon: UserCheck, label: "CRM Leads", color: "text-pink-500" },
  user: { icon: Users, label: "Users", color: "text-cyan-600" },
  role: { icon: Shield, label: "Roles", color: "text-amber-600" },
  dispatch: { icon: Truck, label: "Dispatch Notes", color: "text-indigo-500" },
  bundle: { icon: Layers, label: "Bundles", color: "text-violet-500" },
  company: { icon: Building2, label: "Company", color: "text-emerald-500" },
  navigation: { icon: LayoutDashboard, label: "Navigation", color: "text-primary" },
  task: { icon: CheckSquare, label: "Tasks", color: "text-rose-500" },
  note: { icon: StickyNote, label: "Notes", color: "text-yellow-500" },
};

const RECENT_SEARCHES_KEY = "erp_recent_searches";
const MAX_RECENT = 8;

function getRecentSearches() {
  try {
    return JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) || "[]");
  } catch { return []; }
}

function addRecentSearch(query) {
  if (!query.trim()) return;
  // Stealth: never persist hashtag triggers — they could expose the private
  // ledger trigger or panic phrase if anyone opens this user's browser.
  if (query.trim().startsWith("#")) return;
  const recent = getRecentSearches().filter(r => r !== query && !r.startsWith("#"));
  recent.unshift(query);
  localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
}

function purgeHashtagRecents() {
  try {
    const recent = getRecentSearches().filter(r => !r.startsWith("#"));
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent));
  } catch { /* ignore */ }
}

function clearRecentSearches() {
  localStorage.removeItem(RECENT_SEARCHES_KEY);
}

export default function GlobalSearch({ open, onOpenChange }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savedSearches, setSavedSearches] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [aiPowered, setAiPowered] = useState(false);
  const [aiExplanation, setAiExplanation] = useState("");
  const [trending, setTrending] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const abortRef = useRef(null);
  const suggestRef = useRef(null);
  const navigate = useNavigate();

  const search = useCallback(async (q) => {
    if (!q.trim()) { setResults([]); setAiPowered(false); setAiExplanation(""); setSuggestions([]); return; }

    // Silent trigger check (no UI hint). Runs in parallel with normal search.
    // Min-length 6 gate: avoids a server probe for every '#' keystroke and the
    // partial typings below the minimum valid trigger length.
    if (q.trim().startsWith("#") && q.trim().length >= 6) {
      try {
        const tr = await api.post("/private-ledger/check-trigger", { q: q.trim() });
        if (tr?.data?.matched) {
          const action = tr.data.action;
          if (action === "setup") {
            sessionStorage.setItem("plg_setup_arrival", "1");
            navigate("/p/s/9f3a");
          } else {
            // Default = unlock flow
            sessionStorage.setItem("plg_arrival", "1");
            navigate("/p/9f3a");
          }
          setTimeout(() => {
            try { onOpenChange?.(false); } catch { /* ignore */ }
          }, 0);
          return;
        }
      } catch { /* fail-silent so feature is undiscoverable */ }
    }

    // Cancel previous in-flight request
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    // Auto-cancel after 15 seconds to prevent "Thinking..." forever
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    setLoading(true);
    try {
      // Use the NL-aware endpoint — backend handles detection + AI fallback
      const res = await api.get(`/search/nl?q=${encodeURIComponent(q)}`, { signal: controller.signal });
      if (!controller.signal.aborted) {
        setResults(res.data.results || []);
        setAiPowered(res.data.ai_powered || false);
        setAiExplanation(res.data.explanation || "");
      }
    } catch (e) {
      if (e?.name !== "CanceledError" && e?.code !== "ERR_CANCELED") {
        // Fallback to regular search if NL endpoint fails
        try {
          const fallbackCtrl = new AbortController();
          const fallbackTimeout = setTimeout(() => fallbackCtrl.abort(), 10000);
          const res = await api.get(`/search?q=${encodeURIComponent(q)}`, { signal: fallbackCtrl.signal });
          clearTimeout(fallbackTimeout);
          if (!controller.signal.aborted) {
            setResults(res.data.results || []);
            setAiPowered(false);
            setAiExplanation("");
          }
        } catch {
          if (!controller.signal.aborted) setResults([]);
        }
      }
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  }, []);

  // Fetch auto-suggestions as user types
  const fetchSuggestions = useCallback(async (q) => {
    if (!q.trim() || q.trim().length < 2) { setSuggestions([]); return; }
    if (suggestRef.current) suggestRef.current.abort();
    const ctrl = new AbortController();
    suggestRef.current = ctrl;
    try {
      const res = await api.get(`/search/analytics/suggestions?q=${encodeURIComponent(q)}`, { signal: ctrl.signal });
      if (!ctrl.signal.aborted) setSuggestions(res.data || []);
    } catch {
      if (!ctrl.signal.aborted) setSuggestions([]);
    }
  }, []);

  useEffect(() => {
    const delay = query.split(" ").length > 2 ? 500 : 250;
    const timer = setTimeout(() => search(query), delay);
    return () => clearTimeout(timer);
  }, [query, search]);

  // Fetch suggestions with shorter debounce
  useEffect(() => {
    const timer = setTimeout(() => fetchSuggestions(query), 150);
    return () => clearTimeout(timer);
  }, [query, fetchSuggestions]);

  useEffect(() => {
    if (open) {
      purgeHashtagRecents();   // stealth: drop any legacy '#...' entries
      setRecentSearches(getRecentSearches());
      api.get("/search/saved").then(r => setSavedSearches(r.data || [])).catch(() => {});
      api.get("/search/analytics/trending").then(r => setTrending(r.data || [])).catch(() => {});
    } else {
      setQuery("");
      setResults([]);
      setAiPowered(false);
      setAiExplanation("");
      setSuggestions([]);
    }
  }, [open]);

  const handleSelect = (item) => {
    addRecentSearch(query);
    setRecentSearches(getRecentSearches());
    navigate(item.path);
    onOpenChange(false);
  };

  const handleRecentClick = (q) => {
    setQuery(q);
  };

  const handleSaveSearch = async () => {
    if (!query.trim()) return;
    try {
      await api.post("/search/saved", { query, label: query });
      const r = await api.get("/search/saved");
      setSavedSearches(r.data || []);
    } catch {}
  };

  const handleDeleteSaved = async (id) => {
    try {
      await api.delete(`/search/saved/${id}`);
      setSavedSearches(prev => prev.filter(s => s.id !== id));
    } catch {}
  };

  const handleClearRecent = () => {
    clearRecentSearches();
    setRecentSearches([]);
  };

  // Group results by type
  const grouped = results.reduce((acc, r) => {
    if (!acc[r.type]) acc[r.type] = [];
    acc[r.type].push(r);
    return acc;
  }, {});

  const resultCount = results.length;

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <div className="flex items-center border-b px-3">
        <Search className="w-4 h-4 text-muted-foreground shrink-0" />
        <CommandInput
          placeholder="Search orders, invoices, customers, items, modules..."
          value={query}
          onValueChange={setQuery}
          data-testid="global-search-input"
          className="border-0 focus:ring-0"
        />
        {query && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1 text-xs shrink-0"
            onClick={handleSaveSearch}
            data-testid="save-search-btn"
          >
            <Bookmark className="w-3 h-3" /> Save
          </Button>
        )}
      </div>

      <CommandList className="max-h-[420px]" data-testid="global-search-results">
        {query && !loading && results.length === 0 && (
          <CommandEmpty data-testid="search-empty">No results found for "{query}"</CommandEmpty>
        )}
        {loading && (
          <div className="py-6 text-center text-sm text-muted-foreground" data-testid="search-loading">
            {query.split(" ").length > 2
              ? <span className="inline-flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-amber-500 animate-pulse" /> Thinking...</span>
              : "Searching..."}
          </div>
        )}

        {/* AI explanation banner */}
        {aiPowered && !loading && aiExplanation && (
          <div className="mx-2 mt-1.5 mb-1 px-3 py-2 rounded-md bg-amber-500/10 border border-amber-500/20" data-testid="ai-search-banner">
            <div className="flex items-center gap-1.5 text-xs font-medium text-amber-600 dark:text-amber-400">
              <Sparkles className="w-3.5 h-3.5" />
              AI Search
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">{aiExplanation}</p>
          </div>
        )}

        {/* Result count badge */}
        {query && !loading && resultCount > 0 && (
          <div className="px-3 py-1.5 text-[11px] text-muted-foreground font-medium flex items-center gap-1.5" data-testid="search-result-count">
            {resultCount} result{resultCount !== 1 ? "s" : ""} found
            {aiPowered && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 text-[10px] font-medium">
                <Sparkles className="w-2.5 h-2.5" /> AI
              </span>
            )}
          </div>
        )}

        {/* Auto-suggestions while typing */}
        {query && suggestions.length > 0 && !loading && results.length === 0 && (
          <CommandGroup heading={
            <span className="inline-flex items-center gap-1.5">
              <Zap className="w-3 h-3 text-blue-500" />
              Suggestions
            </span>
          }>
            {suggestions.map((s, i) => (
              <CommandItem
                key={`suggest-${i}`}
                value={`suggest-${s.query}`}
                onSelect={() => setQuery(s.query)}
                className="gap-3 cursor-pointer"
                data-testid={`search-suggestion-${i}`}
              >
                <Search className="w-3.5 h-3.5 text-muted-foreground/50" />
                <span className="text-sm flex-1">{s.query}</span>
                <span className="text-[10px] text-muted-foreground">{s.count}x</span>
                {s.ai_powered && <Sparkles className="w-3 h-3 text-amber-400" />}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Grouped results */}
        {Object.entries(grouped).map(([type, items]) => {
          const config = TYPE_CONFIG[type] || TYPE_CONFIG.navigation;
          const Icon = config.icon;
          return (
            <CommandGroup key={type} heading={
              <span className="inline-flex items-center gap-1.5">
                <Icon className={`w-3 h-3 ${config.color}`} />
                {config.label}
                <span className="text-[10px] text-muted-foreground/60 font-data">({items.length})</span>
              </span>
            }>
              {items.map((item) => (
                <CommandItem
                  key={`${type}-${item.id}`}
                  value={`${item.title} ${item.subtitle}`}
                  onSelect={() => handleSelect(item)}
                  className="gap-3 cursor-pointer"
                  data-testid={`search-result-${type}-${item.id}`}
                >
                  <div className={`w-7 h-7 rounded-md flex items-center justify-center bg-muted/80 ${config.color}`}>
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{item.title}</p>
                    {item.subtitle && (
                      <p className="text-xs text-muted-foreground truncate">{item.subtitle}</p>
                    )}
                  </div>
                  {item.meta && (
                    <span className="text-[10px] text-muted-foreground uppercase tracking-wider shrink-0 px-1.5 py-0.5 rounded bg-muted/60">
                      {item.meta}
                    </span>
                  )}
                  <ArrowRight className="w-3 h-3 text-muted-foreground/40 shrink-0" />
                </CommandItem>
              ))}
            </CommandGroup>
          );
        })}

        {/* When no query — show recent and saved */}
        {!query && (
          <>
            {/* Recent Searches */}
            {recentSearches.length > 0 && (
              <CommandGroup heading={
                <span className="inline-flex items-center gap-1.5 w-full">
                  <Clock className="w-3 h-3 text-muted-foreground" />
                  Recent Searches
                  <button
                    onClick={handleClearRecent}
                    className="ml-auto text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="clear-recent-btn"
                  >
                    Clear
                  </button>
                </span>
              }>
                {recentSearches.map((q, i) => (
                  <CommandItem
                    key={`recent-${i}`}
                    value={`recent-${q}`}
                    onSelect={() => handleRecentClick(q)}
                    className="gap-3 cursor-pointer"
                    data-testid={`recent-search-${i}`}
                  >
                    <Clock className="w-3.5 h-3.5 text-muted-foreground/50" />
                    <span className="text-sm">{q}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {/* Saved Searches */}
            {savedSearches.length > 0 && (
              <CommandGroup heading={
                <span className="inline-flex items-center gap-1.5">
                  <Bookmark className="w-3 h-3 text-amber-500" />
                  Saved Searches
                </span>
              }>
                {savedSearches.map((s) => (
                  <CommandItem
                    key={`saved-${s.id}`}
                    value={`saved-${s.label}`}
                    onSelect={() => handleRecentClick(s.query)}
                    className="gap-3 cursor-pointer group/saved"
                    data-testid={`saved-search-${s.id}`}
                  >
                    <Star className="w-3.5 h-3.5 text-amber-400" />
                    <span className="text-sm flex-1">{s.label}</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteSaved(s.id); }}
                      className="opacity-0 group-hover/saved:opacity-100 p-0.5 rounded hover:bg-muted transition-all"
                      data-testid={`delete-saved-${s.id}`}
                    >
                      <X className="w-3 h-3 text-muted-foreground" />
                    </button>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {/* Trending Searches */}
            {trending.length > 0 && (
              <CommandGroup heading={
                <span className="inline-flex items-center gap-1.5">
                  <TrendingUp className="w-3 h-3 text-rose-500" />
                  Trending in your org
                </span>
              }>
                {trending.map((t, i) => (
                  <CommandItem
                    key={`trending-${i}`}
                    value={`trending-${t.query}`}
                    onSelect={() => handleRecentClick(t.query)}
                    className="gap-3 cursor-pointer"
                    data-testid={`trending-search-${i}`}
                  >
                    <TrendingUp className="w-3.5 h-3.5 text-rose-400/60" />
                    <span className="text-sm flex-1">{t.query}</span>
                    <span className="text-[10px] text-muted-foreground/60">{t.count} searches</span>
                    {t.ai_searches > 0 && <Sparkles className="w-3 h-3 text-amber-400/50" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            <CommandSeparator />

            {/* Quick Navigation */}
            <CommandGroup heading="Quick Navigation">
              {[
                { title: "Dashboard", path: "/dashboard", icon: LayoutDashboard },
                { title: "Sales Orders", path: "/sales", icon: ShoppingCart },
                { title: "Production", path: "/production", icon: Factory },
                { title: "Finance", path: "/finance", icon: Landmark },
                { title: "Inventory", path: "/inventory", icon: Warehouse },
                { title: "CRM Leads", path: "/crm", icon: UserCheck },
                { title: "HR & Payroll", path: "/hr", icon: UserCog },
                { title: "Settings", path: "/settings", icon: Wrench },
              ].map((item) => (
                <CommandItem
                  key={item.path}
                  value={item.title}
                  onSelect={() => { navigate(item.path); onOpenChange(false); }}
                  className="gap-3 cursor-pointer"
                  data-testid={`search-quick-${item.path.replace("/", "") || "dashboard"}`}
                >
                  <item.icon className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-sm">{item.title}</span>
                  <ArrowRight className="w-3 h-3 text-muted-foreground/30 ml-auto" />
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>

      {/* Footer hint */}
      <div className="flex items-center justify-between px-3 py-2 border-t text-[11px] text-muted-foreground" data-testid="search-footer">
        <span className="inline-flex items-center gap-1.5">
          <Sparkles className="w-3 h-3 text-amber-500/70" />
          Try natural language: "overdue invoices from last month"
        </span>
        <div className="flex items-center gap-2">
          <kbd className="px-1.5 py-0.5 rounded bg-muted text-[10px] font-data">ESC</kbd>
          <span>to close</span>
        </div>
      </div>
    </CommandDialog>
  );
}
